import { t } from 'i18next';
import {
  DEFAULT_END_TIME,
  DEFAULT_SCOPE,
  DEFAULT_START_TIME,
} from '../../../domain/fullCalendarHeader/useFullCalendarHeader';
import { formatGlobalDateRangeLabel } from '../../../domain/dateRange/useDateRangeUseCase';
import styles from './FullCalendarHeader.module.css';
import FilterSelect from '../FilterSelect/FilterSelect';
import { Popover } from '../../../store/popover/PopoverContext';
import { PopoverContent, PopoverTrigger } from '../../atoms/Popover/Popover';
import ModalTrigger from '../ModalTrigger/ModalTrigger';
import SearchField from '../SearchField/SearchField';
import TimeRangePicker from '../TimeRangePicker/TimeRangePicker';
import FilterDateRangePicker from '../../organisms/FilterDateRangePicker/FilterDateRangePicker';

/**
 * CalendarHeader component - Used to change settings of the calendar
 * @param {object} props
 * @param {string} [props.startTime]
 * @param {string} [props.endTime]
 * @param {() => void} [props.openTimePicker]
 * @param {(value: string) => void} [props.onStartTimeChange]
 * @param {(value: string) => void} [props.onEndTimeChange]
 * @param {CalendarScope} [props.scope]
 * @param {import('react-date-range').Range[]} [props.ranges]
 * @param {ApiPageTemplate} [props.template]
 * @param {import('../../../domain/dateRange/useDateRange').DateRangeHookReturn} [props.dateRange]
 * @param {(newType: ApiManagerDateRangeType, newRange: import('react-date-range').Range) => void} [props.handleDateRangeMutation]
 * @param {(direction: "minus" | "plus") => void} [props.handlePrevNextValueMutation]
 * @param {(newType: CalendarScope) => void} [props.handleTypeValueChangeMutation]
 * @param {ApiManagerTimelineHeaderFilter[]} [props.headerFilters]
 * @param {React.ReactNode} [props.children]
 * @returns {React.ReactElement}
 */
function CalendarHeader({
  startTime = DEFAULT_START_TIME,
  endTime = DEFAULT_END_TIME,
  openTimePicker,
  onStartTimeChange,
  onEndTimeChange,
  scope = DEFAULT_SCOPE,
  ranges,
  template,
  children,
  dateRange,
  handleDateRangeMutation,
  handlePrevNextValueMutation,
  handleTypeValueChangeMutation,
  headerFilters = [],
}) {
  // Limit to 2 months for calendar header
  const numberMonthDisplayByType = {
    day: 2, week: 2, month: 2, quarter: 2, custom: 2,
  };
  return (
    <div className={styles['calendar-header']}>
      <div className={styles['calendar-settings-row']}>
        {headerFilters.includes('dateRange') ? (
          <Popover open={dateRange.calendarOpen} onOpenChange={dateRange.setCalendarOpen} placement="bottom">
            <PopoverTrigger asChild>
              <ModalTrigger
                icon="calendar-main"
                iconPosition="right"
                title={t('date.openCalendar')}
                label={formatGlobalDateRangeLabel({
                  type: scope,
                  startDate: (ranges?.[0] ?? dateRange.ranges[0]).startDate,
                  endDate: (ranges?.[0] ?? dateRange.ranges[0]).endDate,
                }, t)}
                onClick={() => dateRange.setCalendarOpen(!dateRange.calendarOpen)}
                template={template}
                isLoading={false}
                disabled={false}
                maxWidth="420px"
              />
            </PopoverTrigger>
            <PopoverContent>
              <FilterDateRangePicker
                dateRange={dateRange}
                ranges={ranges}
                type={scope}
                handleDateRangeMutation={handleDateRangeMutation}
                numberMonthDisplayByType={numberMonthDisplayByType}
              />
            </PopoverContent>
          </Popover>
        ) : null}

        {headerFilters.includes('dateRangeTypeOptions') ? (
          <FilterSelect
            options={dateRange.typeOptionsList}
            value={scope}
            handleValue={(/** @type {ApiManagerDateRangeType} */ value) => handleTypeValueChangeMutation(value)}
            hasPrevNext
            handlePrevNextValue={(direction) => handlePrevNextValueMutation(direction)}
            template={template}
            isLoading={false}
            disabled={false}
          >
            {(option) => (
              <option key={option.value} value={option.value}>
                {t(option.label)}
              </option>
            )}
          </FilterSelect>
        ) : null}

        {headerFilters.includes('timeWindows') ? (
          <Popover
            placement="bottom-start"
          >
            <PopoverTrigger asChild>
              <ModalTrigger
                icon="clock"
                iconPosition="right"
                onClick={openTimePicker}
                title="TimePicker"
                label={`${startTime} à ${endTime}`}
              />
            </PopoverTrigger>
            <PopoverContent>
              <TimeRangePicker
                startTime={startTime}
                endTime={endTime}
                onStartTimeChange={onStartTimeChange}
                onEndTimeChange={onEndTimeChange}
                title="Plage horaire"
              />
            </PopoverContent>
          </Popover>
        ) : null}

      </div>
      <div>
        <SearchField
          id="search-events"
          placeholder="Rechercher..."
          onFocus={() => { }}
          onChange={() => { }}
          className={styles['search-field-form']}
        />
        {children}
      </div>
    </div>
  );
}
export default CalendarHeader;
